// The catalog check: when Omni last read Autotask, KQM and OneBill, whether a check
// is running or failed, and Check now (POST /api/sync/runs with the catalog step).
// Under it, the products a check linked to OneBill on its own, each with why (same
// code, or same name) and Undo, then the Product codes report (#2057): where codes
// came from, and every code Omni couldn't use to link, in collapsible lists.
import React, { useState } from "react";
import { formatCents, type CatalogCodeReport, type CodeReportProduct, type SyncStatus } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { plural, stamp } from "../../format.js";
import { Callout, Chip } from "../../ui.js";
import { useCatalog } from "./context.js";
import { autoReason, billedText, clock, kindWord, nextScheduledCheck, type Product } from "./model.js";

type CheckState = "idle" | "running" | "failed";

export function CheckCard({
  status,
  statusError,
  running,
  busyNote,
  reviewCount,
  onCheck,
}: {
  status: SyncStatus | null;
  statusError: string | null;
  /** This page's own Check now is in flight. */
  running: boolean;
  /** Check now found another run holding the lease. */
  busyNote: boolean;
  reviewCount: number;
  /** null for a tier that can't run the check (sync.run): the card says when the next one runs. */
  onCheck: (() => void) | null;
}): React.ReactElement {
  const checked = status?.catalog ?? null;
  const lastRun = status?.lastRun ?? null;
  const failedStep = lastRun?.steps.catalog?.outcome === "failed" && (!checked?.lastOkAt || lastRun.startedAt >= checked.lastOkAt) ? lastRun.steps.catalog : null;
  const state: CheckState = running || status?.inProgress ? "running" : failedStep ? "failed" : "idle";
  const onebillRead = lastRun?.steps.catalog?.read.onebillSummaries;
  const kqmRead = checked?.codes?.kqm;

  let title: string;
  let sub: string;
  if (state === "running") {
    title = "Checking Autotask, KQM and OneBill";
    const remote = lastRun && lastRun.finishedAt === null ? lastRun : null;
    sub = running
      ? "Started by you. This page updates when it finishes."
      : remote
        ? `Started ${clock(remote.startedAt)} ${remote.trigger === "schedule" ? "by the schedule" : `by ${remote.by ?? "Phoneware"}`}. This page updates when it finishes.`
        : "Another check is running. This page updates when it finishes.";
  } else if (state === "failed") {
    title = `Latest check failed${lastRun?.finishedAt ? ` \u00b7 ${stamp(lastRun.finishedAt)}` : ""}`;
    sub = checked?.lastOkAt ? `Showing the check from ${stamp(checked.lastOkAt)}. Nothing from the failed check was saved.` : "Nothing from the failed check was saved.";
  } else {
    title = checked?.lastOkAt ? `Last checked ${stamp(checked.lastOkAt)}` : "Not checked yet";
    sub = "Checks run every 15 minutes. They read Autotask, KQM and OneBill and change nothing in any of them.";
  }

  return (
    <section className="card" data-testid="check-card" data-state={state}>
      <div className="check-row">
        <span className={state === "running" ? "check-ic run" : state === "failed" ? "check-ic bad" : "check-ic"}>
          <Icon name={state === "running" ? "refresh-cw" : state === "failed" ? "triangle-alert" : "circle-check"} size={18} className={state === "running" ? "spin-ic" : undefined} />
        </span>
        <div className="check-main">
          <h2 data-testid="check-title">{title}</h2>
          <p>{sub}</p>
          {state === "running" ? (
            <div className="prog indet" role="progressbar" aria-label="Check progress" data-testid="check-progress">
              <i />
            </div>
          ) : null}
          <div className="check-facts">
            {checked?.autotaskCounts ? (
              <span>
                <b>{checked.autotaskCounts.products.toLocaleString("en-US")}</b> products and <b>{checked.autotaskCounts.services.toLocaleString("en-US")}</b> services in Autotask
              </span>
            ) : null}
            {kqmRead?.read ? (
              <span>
                <b>{kqmRead.products.toLocaleString("en-US")}</b> products in KQM
              </span>
            ) : null}
            {onebillRead !== undefined ? (
              <span>
                <b>{onebillRead.toLocaleString("en-US")}</b> products in OneBill
              </span>
            ) : null}
            <span>
              <b>{reviewCount.toLocaleString("en-US")}</b> in the review queue
            </span>
          </div>
        </div>
        <div className="check-end">
          {state === "running" ? (
            <button className="btn" type="button" disabled>
              <Icon name="refresh-cw" size={15} className="spin-ic" />
              Checking
            </button>
          ) : onCheck ? (
            <button className="btn" type="button" data-testid="check-now" onClick={onCheck}>
              <Icon name="refresh-cw" size={15} />
              {state === "failed" ? "Check again" : "Check now"}
            </button>
          ) : null}
          <span className="small">Next scheduled check {clock(nextScheduledCheck(new Date()))}</span>
        </div>
      </div>
      {statusError ? (
        <div className="check-note">
          <Callout kind="stop" icon="triangle-alert">
            Omni couldn{"\u2019"}t read the check status: {statusError}
          </Callout>
        </div>
      ) : null}
      {state === "failed" && failedStep ? (
        <div className="callout warn check-note" role="alert" data-testid="check-failed">
          <Icon name="triangle-alert" size={15} />
          <span>{failedStep.error ?? "The check stopped without an error message."}</span>
        </div>
      ) : null}
      {busyNote ? (
        <div className="callout check-note" role="status" data-testid="check-busy">
          <Icon name="info" size={15} />
          <span>A check is already running, so Omni didn{"\u2019"}t start another. This page updates when it finishes.</span>
        </div>
      ) : null}
      <AutoLinked />
      {checked?.codes ? <ProductCodes report={checked.codes} /> : null}
    </section>
  );
}

/** The products a check linked to OneBill by itself, three at a time, each with why and Undo. */
function AutoLinked(): React.ReactElement {
  const { list, decide, busy, links } = useCatalog();
  const [all, setAll] = useState(false);
  const linked = list.filter((p) => p.billingLink.state === "linked" && p.billingLink.via === "auto" && p.onebill);
  const byCode = linked.filter((p) => p.billingLink.tier === "code");
  const byName = linked.filter((p) => p.billingLink.tier !== "code");
  // Links by code first, the way the report counts them.
  const ordered = [...byCode, ...byName];
  const shown = all ? ordered : ordered.slice(0, 3);
  return (
    <div className="auto" data-testid="auto-linked">
      <div className="sect-h">
        <h3>Linked automatically</h3>
        <Chip kind="ok" testId="auto-by-code">
          {byCode.length.toLocaleString("en-US")} by code
        </Chip>
        <Chip kind="quiet" testId="auto-by-name">
          {byName.length.toLocaleString("en-US")} by name
        </Chip>
        <span className="note hide-sm">The same product code in both systems, or before codes, the same name, and one OneBill plan that bills the same way</span>
      </div>
      {linked.length === 0 ? (
        <p className="empty">No product is linked automatically right now. Products a check links on its own show up here, with Undo.</p>
      ) : (
        <div data-testid="auto-list">
          {shown.map((p) => {
            const why = autoReason(p);
            return (
              <div key={p.id} className="auto-row" data-testid={`auto-${p.id}`}>
                <div>
                  <b>{p.name}</b>
                  <span className="code">
                    {kindWord(p)} {p.autotask?.id ?? "not created yet"} · {billedText(p)} · {formatCents(p.listPrice)}
                  </span>
                  <span className="pc-reason">
                    <Chip kind={why.kind} icon={why.icon} testId={`reason-${p.id}`}>
                      {why.label}
                    </Chip>
                  </span>
                </div>
                <span className="arr">
                  <Icon name="link" size={15} />
                </span>
                <div>
                  <b>{p.onebill?.productCode}</b>
                  <span className="code">plan {p.onebill?.pricePlanId}</span>
                </div>
                {links ? (
                  <button className="btn small white" type="button" disabled={busy} data-testid={`undo-${p.id}`} onClick={() => void decide(p, "unreviewed")}>
                    <Icon name="undo-2" size={14} />
                    Undo
                  </button>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
        </div>
      )}
      {linked.length > 3 ? (
        <button className="link more" type="button" onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show all ${plural(linked.length, "product")}`}
        </button>
      ) : null}
    </div>
  );
}

const NO_PLAN_WHY: Record<CatalogCodeReport["noPlan"][number]["reason"], (productCode: string) => string> = {
  billing_differs: (c) => `${c} has no active plan that bills the way this product does.`,
  several_plans: (c) => `${c} has more than one active plan that bills the same way, so a person picks one.`,
  no_amount: (c) => `${c}\u2019s plan has no charge set.`,
  no_billing: () => "This product doesn\u2019t bill monthly or once, so no OneBill plan fits.",
};

/** A list stays short until asked: the first ten rows, then Show all. */
const FIRST = 10;

/**
 * The Product codes report the check writes (CatalogSyncState.codes): where codes
 * came from, how many linked by code, and a collapsible list for each kind of code
 * Omni couldn't use. Every product name opens its sheet.
 */
function ProductCodes({ report }: { report: CatalogCodeReport }): React.ReactElement {
  const { list, open, decide, busy, links } = useCatalog();
  const byId = new Map(list.map((p) => [p.id, p]));
  const kqmFailed = !report.kqm.read;
  const none = Math.max(0, list.length - report.withCode.autotask - report.withCode.kqm);

  const name = (r: CodeReportProduct) =>
    byId.has(r.id) ? (
      <button key={r.id} className="link" type="button" data-testid={`codes-open-${r.id}`} onClick={() => open(r.id)}>
        {r.name}
      </button>
    ) : (
      <span key={r.id}>{r.name}</span>
    );
  const names = (rs: CodeReportProduct[]) => (
    <span className="pc-inl">
      {rs.map((r, i) => (
        <React.Fragment key={r.id}>
          {i > 0 ? (i === rs.length - 1 ? " and " : ", ") : null}
          {name(r)}
        </React.Fragment>
      ))}
    </span>
  );

  const differs = report.linkDiffers.map((d) => {
    const p: Product | undefined = byId.get(d.product.id);
    const stillAuto = p?.billingLink.state === "linked" && p.billingLink.via === "auto";
    const how = d.via === "ops" ? `Linked by ops to ${d.linked}.` : `Linked automatically to ${d.linked} by name.`;
    const would = d.byCode ? `By code it would be ${d.byCode}.` : `OneBill has no product with code ${d.code}.`;
    const undone = d.via === "auto" && p && !stillAuto;
    return {
      key: d.product.id,
      code: d.code,
      main: name(d.product),
      extra: undone ? "Undone. It\u2019s in the review queue." : `${how} ${would}`,
      act:
        d.via !== "auto" || !p ? null : undone ? (
          <Chip kind="quiet" testId={`codes-undone-${d.product.id}`}>
            Undone
          </Chip>
        ) : links ? (
          <button className="btn small white" type="button" disabled={busy} data-testid={`codes-undo-${d.product.id}`} onClick={() => void decide(p, "unreviewed")}>
            <Icon name="undo-2" size={14} />
            Undo
          </button>
        ) : null,
    };
  });

  return (
    <div className="pc-codes" data-testid="codes-block">
      <div className="sect-h pc-sep">
        <h3>Product codes</h3>
        <span className="note">Read from Autotask, then KQM. Omni never edits a code; change it in Autotask or KQM.</span>
      </div>
      {kqmFailed ? (
        <div className="callout warn" role="alert" data-testid="kqm-failed">
          <Icon name="triangle-alert" size={15} />
          <div className="cbody">
            <b>Omni couldn{"\u2019"}t read KQM{"\u2019"}s products this check.</b> {report.kqm.error ? <>{report.kqm.error} </> : null}Codes from Autotask are current; the{" "}
            {report.withCode.kqm.toLocaleString("en-US")} {report.withCode.kqm === 1 ? "code" : "codes"} from KQM are kept from the last check that read KQM, and none were cleared.
          </div>
        </div>
      ) : null}
      <div className="pc-sum" data-testid="codes-summary">
        <Sum n={report.withCode.autotask} label="From Autotask" sub="Manufacturer field, else SKU" testId="sum-autotask" />
        <Sum n={report.withCode.kqm} label={kqmFailed ? "From KQM, kept from an earlier check" : "From KQM"} sub="Part number, matched by exact title" testId="sum-kqm" />
        <Sum n={none} label="No code" sub="Neither has one" testId="sum-none" />
        <Sum n={report.linkedByCode} label="Linked by code" sub="To the OneBill product with that code" testId="sum-linked" />
      </div>

      <Det id="noOneBill" title="Codes OneBill has no product for" n={report.noOneBill.length} empty="Every product code has a OneBill product.">
        <p className="hint">Omni can{"\u2019"}t link these by code. A person links each one, or chooses Create in OneBill.</p>
        <Rows rows={report.noOneBill.map((r) => ({ key: r.code, code: r.code, main: names(r.products) }))} />
      </Det>
      <Det id="ambiguous" title="Codes on more than one OneBill product" n={report.ambiguous.length} empty={"No product\u2019s code matches two OneBill products."}>
        <p className="hint">Omni links by code only when OneBill has one product with it. A person picks which one.</p>
        <Rows rows={report.ambiguous.map((r) => ({ key: r.code, code: r.code, main: names(r.products), extra: `OneBill has ${r.productCodes.join(" and ")}.` }))} />
      </Det>
      <Det id="noPlan" title="Same code, but no plan Omni can use" n={report.noPlan.length} empty={"Every OneBill product with a product\u2019s code has one plan Omni can use."}>
        <Rows rows={report.noPlan.map((r) => ({ key: `${r.code}-${r.productCode}`, code: r.code, main: names(r.products), extra: NO_PLAN_WHY[r.reason](r.productCode) }))} />
      </Det>
      <Det id="linkDiffers" title="Links that disagree with the code" n={report.linkDiffers.length} empty={"Every link agrees with its product\u2019s code."}>
        <p className="hint">Omni never changes a link on its own. Undo sends an automatic link back to the review queue, where the same-code product is first.</p>
        <Rows rows={differs} />
      </Det>
      <Det id="reviewed" title="Decided by ops, code points elsewhere" n={report.reviewed.length} empty="No product ops decided by hand has a code that points to a OneBill product.">
        <p className="hint">Ops decided these by hand, so Omni leaves them alone. Each one{"\u2019"}s code matches a OneBill product.</p>
        <Rows rows={report.reviewed.map((r) => ({ key: r.product.id, code: r.code, main: name(r.product), extra: `OneBill has ${r.productCode} for this code.` }))} />
      </Det>
      <Det id="shared" title="Codes on more than one Omni product" n={report.shared.length} kind="quiet" empty="No code is on more than one Omni product.">
        <p className="hint">Allowed. Both products bill on the same OneBill product. A quote line with the code goes to the one with its name, else the one dealers can quote.</p>
        <Rows rows={report.shared.map((r) => ({ key: r.code, code: r.code, main: names(r.products) }))} />
      </Det>
      <Det id="conflicts" title="Autotask and KQM disagree" n={report.conflicts.length} empty="Autotask and KQM agree on every code they both have.">
        <p className="hint">Omni uses Autotask{"\u2019"}s code. To settle one, change it in Autotask or in KQM.</p>
        <Rows
          rows={report.conflicts.map((r) => ({
            key: r.product.id,
            code: r.autotask || r.kqm.join(", "),
            main: name(r.product),
            extra: r.autotask ? `Autotask says ${r.autotask}, KQM says ${r.kqm.join(" or ")}.` : `Autotask has no code; KQM has ${r.kqm.join(" and ")} for this title, so Omni uses none.`,
          }))}
        />
      </Det>
    </div>
  );
}

function Sum({ n, label, sub, testId }: { n: number; label: string; sub: string; testId: string }): React.ReactElement {
  return (
    <div data-testid={testId}>
      <span className="n">{n.toLocaleString("en-US")}</span>
      <b>{label}</b>
      <span>{sub}</span>
    </div>
  );
}

function Det({ id, title, n, kind = "warn", empty, children }: { id: string; title: string; n: number; kind?: "warn" | "quiet"; empty: string; children: React.ReactNode }): React.ReactElement {
  return (
    <details className="pc-det" data-testid={`det-${id}`}>
      <summary data-testid={`det-${id}-toggle`}>
        <Icon name="chevron-right" size={14} className="pc-chev" />
        <b>{title}</b>
        <span className={n ? `chip ${kind}` : "chip quiet"} data-testid={`det-${id}-n`}>
          {n.toLocaleString("en-US")}
        </span>
      </summary>
      <div className="pc-det-b">{n ? children : <p className="empty">{empty}</p>}</div>
    </details>
  );
}

interface Row {
  key: string;
  code: string;
  main: React.ReactNode;
  extra?: string;
  act?: React.ReactNode;
}

function Rows({ rows }: { rows: Row[] }): React.ReactElement {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, FIRST);
  return (
    <>
      {shown.map((r) => (
        <div key={r.key} className="pc-rrow" data-testid={`codes-row-${r.key}`}>
          <span className="code pc-code">{r.code}</span>
          <span className="pc-rname">
            {r.main}
            {r.extra ? <small>{r.extra}</small> : null}
          </span>
          {r.act ?? null}
        </div>
      ))}
      {rows.length > FIRST ? (
        <button className="link more" type="button" onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show all ${rows.length.toLocaleString("en-US")}`}
        </button>
      ) : null}
    </>
  );
}
